import PropTypes from 'prop-types';
import React from 'react';
import { Field, FieldArray, Fields } from 'redux-form/immutable';
import { FormControl, IconButton, InputLabel, MenuItem } from '@material-ui/core';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';

import { DeedType } from 'generated/graphql';
import { DurationUnit } from 'entities/deed/role';
import FormDateWithStartAndEndTime from 'retired/FormDateWithStartAndEndTime';
import FormValueSelector from 'retired/FormValueSelector';
import FormTextField from 'retired/FormTextField';
import makeSortableItem from 'retired/SortableItem';
import * as validators from 'utils/validators';
import FormSelect from 'retired/FormSelect';
import FormCheckbox from 'retired/FormCheckbox';

const required = validators.required();
const maxLength3 = validators.maxLength(3);
const maxLength50 = validators.maxLength(50);
const maxLength1000 = validators.maxLength(1000);

const DurationUnitSelector = (props) => (
  <FormSelect fullWidth {...props}>
    <MenuItem value={DurationUnit.hours}>Hours</MenuItem>
    <MenuItem value={DurationUnit.days}>Days</MenuItem>
    <MenuItem value={DurationUnit.weeks}>Weeks</MenuItem>
    <MenuItem value={DurationUnit.months}>Months</MenuItem>
  </FormSelect>
);

const FormRole = ({
  role,
  remove,
  skills,
  deed,
  timeZone,
  selectedDeedType,
  formValues,
  disabled,
}) => {
  const deedType = selectedDeedType ?? deed?.type;

  return (
    <div
      key={role}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        border: '1px solid rgba(0, 0, 0, 0.2)',
        padding: 15,
      }}
    >
      <div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <DragHandleIcon />
        </div>

        {[DeedType.Event, DeedType.BaseEvent].includes(deedType) ? (
          <>
            <Field
              name={`${role}.name`}
              component={FormTextField}
              disabled={disabled}
              fullWidth
              style={{ marginBottom: 10 }}
              label="Shift Name *"
              helperText="50 characters max"
              validate={[required, maxLength50]}
              inputProps={{ maxLength: 50, autoComplete: 'off' }}
            />
            <Field
              name={`${role}.description`}
              component={FormTextField}
              disabled={disabled}
              fullWidth
              style={{ marginBottom: 10 }}
              label="Shift Description"
              helperText="1000 characters max"
              multiline
              inputProps={{ maxLength: 1000 }}
              validate={maxLength1000}
            />
            <Field
              name={`${role}.requiredAmount`}
              component={FormTextField}
              disabled={disabled}
              fullWidth
              type="number"
              inputProps={{ min: '0', step: 1 }}
              style={{ marginBottom: 10 }}
              label="How many people will be needed? *"
              validate={required}
            />
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel disabled={disabled} shrink>
                Shift start and end time *
              </InputLabel>
            </FormControl>
            <Fields
              names={[`${role}.startingAt`, `${role}.endingAt`]}
              isPreviousDateDisabled
              isNextDateDisabled
              component={FormDateWithStartAndEndTime}
              defaults={[
                formValues?.startingAt ?? deed?.startingAt,
                formValues?.endingAt ?? deed?.endingAt,
              ]}
              disabled={disabled}
              timeZone={timeZone}
              timeZoneDisabled
              minDate={formValues?.startingAt ?? deed?.startingAt}
              maxDate={formValues?.endingAt ?? deed?.endingAt}
            />
          </>
        ) : null}

        {[DeedType.Project].includes(deedType) ? (
          <>
            <Field
              name={`${role}.name`}
              component={FormTextField}
              disabled={disabled}
              fullWidth
              style={{ marginBottom: 10 }}
              label="Role Name *"
              helperText="50 characters max"
              validate={[required, maxLength50]}
              inputProps={{ maxLength: 50, autoComplete: 'off' }}
            />
            <Field
              name={`${role}.description`}
              component={FormTextField}
              disabled={disabled}
              fullWidth
              style={{ marginBottom: 10 }}
              label="Role Description"
              helperText="1000 characters max"
              multiline
              inputProps={{ maxLength: 1000 }}
              validate={maxLength1000}
            />
            <Field
              name={`${role}.requiredAmount`}
              component={FormTextField}
              disabled={disabled}
              fullWidth
              type="number"
              inputProps={{ min: '0', step: 1 }}
              style={{ marginBottom: 10 }}
              label="Number of people required *"
              validate={required}
            />
            <FieldArray
              name={`${role}.skills`}
              component={FormValueSelector}
              disabled={disabled}
              selectMultiple
              options={skills}
              labelText="Which skills are needed for this role?"
              validate={[maxLength3]}
            />
            <div style={{ display: 'flex', marginTop: 20, marginBottom: 10 }}>
              <Field
                name={`${role}.estimatedHoursMin`}
                component={FormTextField}
                disabled={disabled}
                fullWidth
                type="number"
                style={{ marginRight: 10 }}
                inputProps={{ min: '0', step: '0.5' }}
                label="Minimum hours *"
                helperText="Minimum total amount of hours?"
                validate={required}
              />
              <Field
                name={`${role}.estimatedHoursMax`}
                component={FormTextField}
                disabled={disabled}
                fullWidth
                type="number"
                style={{ marginRight: 10 }}
                inputProps={{ min: '0', step: '0.5' }}
                label="Maximum hours *"
                helperText="Maximum total amount of hours?"
                validate={required}
              />
              <Field
                name={`${role}.estimatedValue`}
                component={FormTextField}
                disabled={disabled}
                fullWidth
                type="number"
                inputProps={{ min: '0', step: 'any' }}
                label="Estimated value *"
                helperText="How much is each hour valued at in USD?"
                validate={required}
              />
            </div>
            <div style={{ display: 'flex' }}>
              <Field
                name={`${role}.durationMin`}
                component={FormTextField}
                disabled={disabled}
                fullWidth
                type="number"
                style={{ marginRight: 10 }}
                inputProps={{ min: '0', step: '0.5' }}
                label="Minimum duration *"
                helperText="Minimum total duration?"
                validate={required}
              />
              <Field
                name={`${role}.durationMax`}
                component={FormTextField}
                disabled={disabled}
                fullWidth
                type="number"
                style={{ marginRight: 10 }}
                inputProps={{ min: '0', step: '0.5' }}
                label="Maximum duration *"
                helperText="Maximum total duration?"
                validate={required}
              />
              <Field
                name={`${role}.durationUnit`}
                component={DurationUnitSelector}
                disabled={disabled}
                label="Duration in"
              />
            </div>
            <div style={{ margin: '20px 0' }}>
              <Field
                name={`${role}.autoApproval`}
                component={FormCheckbox}
                disabled={disabled}
                label="Select if applications to this role do not require approval"
              />
            </div>
          </>
        ) : null}
      </div>
      <IconButton title="Remove" disabled={disabled} onClick={remove}>
        <DeleteIcon />
      </IconButton>
    </div>
  );
};

FormRole.propTypes = {
  disabled: PropTypes.bool,
  role: PropTypes.string,
  deed: PropTypes.object,
  formValues: PropTypes.object,
  timeZone: PropTypes.string,
  skills: PropTypes.object,
  selectedDeedType: PropTypes.string,
  remove: PropTypes.func,
};

export default makeSortableItem({
  itemType: 'formRole',
  wrapperStyle: {
    paddingTop: 6,
    paddingBottom: 2,
  },
})(FormRole);
